import { useMemo, useRef, useState } from "react";
import { Pagination, message } from "antd";
import {
  BarChartOutlined,
  DownloadOutlined,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { Language } from "../../types";
import type { textMap } from "../../i18n";
import {
  sortStudioScansByTime,
  type StoredStudioScan,
} from "./studioScanHistoryStorage";
import { downloadJson, downloadText } from "../../utils/exportFile";
import {
  formatUiDateTimeFull,
  formatUiTableDateTimeParts,
} from "../../utils/uiDateFormat";
import "../../styles/studio-scan-records.css";

type T = (typeof textMap)["en"];

type RiskTier = "critical" | "high" | "medium" | "low";

function tierFromScore(score: number): RiskTier {
  const s = Math.max(0, Math.min(100, score));
  if (s < 30) return "critical";
  if (s < 55) return "high";
  if (s < 75) return "medium";
  return "low";
}

type RiskFilter = "all" | "high" | "medium" | "low";

function rowMatchesFilter(tier: RiskTier, f: RiskFilter): boolean {
  if (f === "all") return true;
  if (f === "high") return tier === "critical" || tier === "high";
  if (f === "medium") return tier === "medium";
  return tier === "low";
}

type Props = {
  lang: Language;
  t: T;
  rows: StoredStudioScan[];
  onOpenDetail: (row: StoredStudioScan) => void;
};

export function StudioScanRecordsPanel({ lang, t, rows, onOpenDetail }: Props) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState("");
  const [riskFilter, setRiskFilter] = useState<RiskFilter>("all");
  const [page, setPage] = useState(1);
  const pageSize = 5;

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = sortStudioScansByTime(rows);
    return list.filter((row) => {
      const tier = tierFromScore(row.result.score);
      if (!rowMatchesFilter(tier, riskFilter)) return false;
      if (!needle) return true;
      const name = (row.internalName || "").toLowerCase();
      const id = row.scanDisplayId.toLowerCase();
      return name.includes(needle) || id.includes(needle);
    });
  }, [rows, q, riskFilter]);

  const total = filtered.length;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const healthPct = useMemo(() => {
    if (rows.length === 0) return 0;
    return Math.round(
      rows.reduce((a, r) => a + r.result.score, 0) / rows.length,
    );
  }, [rows]);

  const toggleRisk = (f: RiskFilter) => {
    setRiskFilter((prev) => (prev === f ? "all" : f));
    setPage(1);
  };

  const rangeLabel =
    lang === "zh"
      ? `显示 ${start}–${end}，共 ${total} 条记录`
      : `Showing ${start} to ${end} of ${total} records`;

  const riskBadge = (tier: RiskTier) => {
    if (tier === "critical")
      return (
        <span className="studio-rec-badge studio-rec-badge-critical">
          {t.studioRecordsTagCritical}
        </span>
      );
    if (tier === "high")
      return (
        <span className="studio-rec-badge studio-rec-badge-high">
          {t.studioRecordsTagHighRisk}
        </span>
      );
    if (tier === "medium")
      return (
        <span className="studio-rec-badge studio-rec-badge-medium">
          {t.studioRecordsTagMedium}
        </span>
      );
    return (
      <span className="studio-rec-badge studio-rec-badge-secure">
        {t.studioRecordsTagSecure}
      </span>
    );
  };

  const scoreStyle = (tier: RiskTier) => {
    if (tier === "critical" || tier === "high")
      return { color: "var(--sr-error)", fill: "var(--sr-error)" };
    if (tier === "medium")
      return { color: "var(--sr-amber-text)", fill: "var(--sr-amber)" };
    return { color: "var(--sr-tertiary)", fill: "var(--sr-tertiary)" };
  };

  return (
    <div className="studio-rec-root">
      <div className="studio-rec-header-row">
        <div>
          <h1 className="studio-rec-title studio-rec-headline">
            {t.studioRecordsHeadline}
          </h1>
          <p className="studio-rec-sub">{t.studioRecordsSyncLine}</p>
        </div>
        <button
          type="button"
          className="studio-rec-export-btn"
          onClick={() => {
            if (!rows.length) {
              message.warning(t.studioRecordsNoData);
              return;
            }
            downloadJson(`xtool-studio-scans-${Date.now()}.json`, {
              exportedAt: new Date().toISOString(),
              scans: sortStudioScansByTime(rows),
            });
            message.success(t.studioRecordsExported);
          }}
        >
          <DownloadOutlined />
          {t.studioRecordsExportAll}
        </button>
      </div>

      <div className="studio-rec-toolbar">
        <div className="studio-rec-toolbar-left">
          <div className="studio-rec-search-wrap">
            <SearchOutlined />
            <input
              ref={searchRef}
              className="studio-rec-search"
              placeholder={t.studioRecordsSearchPh}
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              aria-label={t.studioRecordsSearchPh}
            />
          </div>
          <button
            type="button"
            className="studio-rec-filters-btn"
            onClick={() => searchRef.current?.focus()}
          >
            <FilterOutlined />
            {t.studioRecordsFilters}
          </button>
        </div>
        <div className="studio-rec-risk-row">
          <span className="studio-rec-risk-label">
            {t.studioRecordsRiskLevelLabel}
          </span>
          <button
            type="button"
            className={`studio-rec-pill studio-rec-pill-high${riskFilter === "high" ? " on" : ""}`}
            onClick={() => toggleRisk("high")}
          >
            {t.studioRecordsPillHigh}
          </button>
          <button
            type="button"
            className={`studio-rec-pill studio-rec-pill-med${riskFilter === "medium" ? " on" : ""}`}
            onClick={() => toggleRisk("medium")}
          >
            {t.studioRecordsPillMedium}
          </button>
          <button
            type="button"
            className={`studio-rec-pill studio-rec-pill-low${riskFilter === "low" ? " on" : ""}`}
            onClick={() => toggleRisk("low")}
          >
            {t.studioRecordsPillLow}
          </button>
        </div>
      </div>

      <div className="studio-rec-table-card">
        <div className="studio-rec-table-scroll">
          <table className="studio-rec-table">
            <thead>
              <tr>
                <th>{t.studioRecordsThTime}</th>
                <th>{t.studioRecordsThProduct}</th>
                <th>{t.studioRecordsThScanId}</th>
                <th style={{ textAlign: "center" }}>
                  {t.studioRecordsThCompliance}
                </th>
                <th>{t.studioRecordsThRiskTag}</th>
                <th style={{ textAlign: "right" }}>
                  {t.studioRecordsThActions}
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: 32,
                      textAlign: "center",
                      color: "var(--sr-secondary)",
                    }}
                  >
                    {t.studioRecordsNoData}
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => {
                  const tier = tierFromScore(row.result.score);
                  const sc = scoreStyle(tier);
                  const score = row.result.score;
                  const { date: dateStr, time: timeStr } =
                    formatUiTableDateTimeParts(row.completedAt, lang);
                  const product =
                    row.internalName.trim() ||
                    (lang === "zh" ? "（未命名）" : "(Untitled)");
                  return (
                    <tr key={row.scanDisplayId}>
                      <td>
                        <div className="studio-rec-time-date">{dateStr}</div>
                        <div className="studio-rec-time-hms">{timeStr}</div>
                      </td>
                      <td>
                        <div className="studio-rec-product" title={product}>
                          {product}
                        </div>
                      </td>
                      <td>
                        <code className="studio-rec-id-code">
                          {row.scanDisplayId}
                        </code>
                      </td>
                      <td>
                        <div className="studio-rec-score-cell">
                          <span
                            className="studio-rec-score-val"
                            style={{ color: sc.color }}
                          >
                            {score}/100
                          </span>
                          <div className="studio-rec-score-bar">
                            <div
                              className="studio-rec-score-fill"
                              style={{
                                width: `${score}%`,
                                background: sc.fill as string,
                              }}
                            />
                          </div>
                        </div>
                      </td>
                      <td>{riskBadge(tier)}</td>
                      <td>
                        <button
                          type="button"
                          className="studio-rec-action"
                          onClick={() => onOpenDetail(row)}
                        >
                          {t.studioRecordsViewDetail}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {total > 0 ? (
          <div className="studio-rec-pagination-bar">
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--sr-secondary)",
              }}
            >
              {rangeLabel}
            </span>
            <Pagination
              size="small"
              current={page}
              pageSize={pageSize}
              total={total}
              onChange={(p) => setPage(p)}
              showSizeChanger={false}
            />
          </div>
        ) : null}
      </div>

      <div className="studio-rec-bento">
        <div className="studio-rec-batch">
          <div style={{ position: "relative", zIndex: 1 }}>
            <h3>{t.studioRecordsBatchTitle}</h3>
            <p>{t.studioRecordsBatchDesc}</p>
            <button
              type="button"
              className="studio-rec-batch-cta"
              onClick={() => {
                if (!rows.length) {
                  message.warning(t.studioRecordsNoData);
                  return;
                }
                const sorted = sortStudioScansByTime(rows);
                const exportedAt = formatUiDateTimeFull(Date.now(), lang);
                const header =
                  lang === "zh"
                    ? `XTool Studio 批量摘要\n导出时间：${exportedAt}\n共 ${sorted.length} 条扫描\n`
                    : `XTool Studio batch summary\nExported: ${exportedAt}\n${sorted.length} scan(s)\n`;
                const lines = sorted.map((r, i) => {
                  const when = new Date(r.completedAt).toISOString();
                  const name = (r.internalName || "—").replace(/\r?\n/g, " ");
                  return `${i + 1}. ${r.scanDisplayId}\t${name}\t${r.result.score}/100\t${when}`;
                });
                downloadText(
                  `xtool-studio-batch-summary-${Date.now()}.txt`,
                  `${header}\n${lines.join("\n")}\n`,
                );
                message.success(t.studioRecordsBatchSummaryOk);
              }}
            >
              {t.studioRecordsBatchCta}
            </button>
          </div>
          <BarChartOutlined className="studio-rec-batch-deco" />
        </div>
        <div className="studio-rec-health">
          <div className="studio-rec-health-label">
            {t.studioRecordsHealthTitle}
          </div>
          <div className="studio-rec-health-pct">{healthPct}%</div>
          <p className="studio-rec-health-desc">{t.studioRecordsHealthDesc}</p>
        </div>
      </div>
    </div>
  );
}
